"use client";

import * as React from "react";
import { CentralizedDrawer } from "@wtc/ui";

interface MobileDrawerProps {
  children: React.ReactNode;
  content: React.ReactNode;
  title?: string;
  className?: string;
  drawerSide: "top" | "bottom" | "left" | "right";
  showCloseButton: boolean;
  stickySubmit?: React.ReactNode;
  asChild: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Mobile drawer rendering branch of ResponsivePopover. Preserves the original
 * JSX exactly, including the `asChild` cloneElement path and the wrapper div
 * fallback path.
 */
export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  children,
  content,
  title,
  className,
  drawerSide,
  showCloseButton,
  stickySubmit,
  asChild,
  onOpenChange,
}) => {
  if (asChild && React.isValidElement(children)) {
    const childElement = children as React.ReactElement<{
      onClick?: (e: React.MouseEvent) => void;
      onMouseDown?: (e: React.MouseEvent) => void;
    }>;
    return (
      <>
        {React.cloneElement(childElement, {
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onOpenChange?.(true);
            childElement.props?.onClick?.(e);
          },
          onMouseDown: (e: React.MouseEvent) => {
            e.stopPropagation();
            childElement.props?.onMouseDown?.(e);
          },
        })}
        <CentralizedDrawer
          drawerId="responsive-popover-drawer"
          side={drawerSide}
          title={title}
          showCloseButton={showCloseButton}
          enableSwipeGesture={true}
          className={className}
        >
          <div className="flex-1 overflow-y-auto p-4">{content}</div>

          {stickySubmit && <div className="border-t p-4">{stickySubmit}</div>}
        </CentralizedDrawer>
      </>
    );
  }

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange?.(true);
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
      <CentralizedDrawer
        drawerId="responsive-popover-drawer"
        side={drawerSide}
        title={title}
        showCloseButton={showCloseButton}
        enableSwipeGesture={true}
        className={className}
      >
        <div className="flex-1 overflow-y-auto p-4">{content}</div>

        {stickySubmit && <div className="border-t p-4">{stickySubmit}</div>}
      </CentralizedDrawer>
    </>
  );
};
